import dayjs from 'dayjs';

import { AssignmentType } from '../../types';

type ActivePeriod = AssignmentType['activePeriods'][number];

/**
 * Finds the active period that best represents a specific fragment's date range.
 *
 * For skip-weekday assignments each fragment maps to a period by exact date match.
 * For continuous assignments or fragments clipped around time-offs, the fragment's
 * bounds won't exactly match a period, so we fall back to the period whose range
 * contains the fragment's start date. Only if no period contains it at all do we
 * fall back to the period containing today, then to the last period.
 */
export function findFragmentActivePeriod(
  activePeriods: ActivePeriod[] | undefined,
  fragmentStart: dayjs.Dayjs,
  fragmentEnd: dayjs.Dayjs
): ActivePeriod | null {
  if (!activePeriods?.length) return null;

  const today = dayjs();

  return (
    activePeriods.find(
      p => p.startDate.isSame(fragmentStart, 'day') && p.endDate.isSame(fragmentEnd, 'day')
    ) ??
    activePeriods.find(
      p =>
        (fragmentStart.isAfter(p.startDate, 'day') || fragmentStart.isSame(p.startDate, 'day')) &&
        (fragmentStart.isBefore(p.endDate, 'day') || fragmentStart.isSame(p.endDate, 'day'))
    ) ??
    activePeriods.find(
      p =>
        (today.isAfter(p.startDate, 'day') || today.isSame(p.startDate, 'day')) &&
        (today.isBefore(p.endDate, 'day') || today.isSame(p.endDate, 'day'))
    ) ??
    activePeriods[activePeriods.length - 1]
  );
}

/**
 * Selects the effective active period for rate display based on the assignment's lifecycle:
 * - Assignment already ended → last period
 * - Assignment hasn't started yet → first period
 * - Assignment is ongoing → period containing today, falling back to the last period
 */
export function findEffectiveActivePeriod(
  activePeriods: ActivePeriod[] | undefined,
  assignmentStart: string,
  assignmentEnd: string
): ActivePeriod | null {
  if (!activePeriods?.length) return null;

  const today = dayjs();
  const start = dayjs(assignmentStart);
  const end = dayjs(assignmentEnd);

  if (end.isBefore(today, 'day')) {
    return activePeriods[activePeriods.length - 1];
  }

  if (start.isAfter(today, 'day')) {
    return activePeriods[0];
  }

  return (
    activePeriods.find(
      p =>
        (today.isAfter(p.startDate, 'day') || today.isSame(p.startDate, 'day')) &&
        (today.isBefore(p.endDate, 'day') || today.isSame(p.endDate, 'day'))
    ) ?? activePeriods[activePeriods.length - 1]
  );
}
